/**
 * Mobile navigation — full-screen overlay with drill-down sub-levels.
 *
 * WCAG 2.1 AA:
 *  - Panel has role="dialog" + aria-modal="true" + aria-label (set in PHP).
 *  - Hamburger toggle uses aria-expanded + aria-controls.
 *  - Focus moves to first focusable on open, back button on drill-down.
 *  - Focus trapped per-level.
 *  - Escape closes sub-level first, then the whole nav.
 *  - Focus returns to toggle on close.
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

function visibleFocusable(root) {
  return [...root.querySelectorAll(FOCUSABLE)].filter(
    (el) => el.offsetParent !== null
  );
}

function initMobileNav() {
  const toggle = document.querySelector('.mobile-nav-toggle');
  const panel = document.getElementById('mobile-nav-panel');

  if (!toggle || !panel) return;

  const rootLevel = panel.querySelector('.mobile-nav-level--root');
  const subLevels = panel.querySelectorAll('.mobile-nav-level--sub');
  const drillTriggers = panel.querySelectorAll('.mobile-nav-link.has-children');
  const backBtns = panel.querySelectorAll('.mobile-nav-back');
  const utilBtns = panel.querySelectorAll('.mobile-nav-util-btn[data-action]');

  let activeSubLevel = null;
  let activeTrigger = null;
  let savedScrollY = 0;
  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  // ── Helpers ──

  function lockScroll() {
    savedScrollY = window.scrollY;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  }

  function unlockScroll() {
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    window.scrollTo(0, savedScrollY);
  }

  function getActiveLevel() {
    return activeSubLevel || rootLevel;
  }

  // ── Open / Close ──

  function openNav() {
    panel.hidden = false;
    // Force reflow so the transition fires.
    void panel.offsetHeight;
    panel.classList.add('is-open');

    toggle.classList.add('is-active');
    toggle.setAttribute('aria-expanded', 'true');
    toggle.setAttribute('aria-label', 'Close menu');

    lockScroll();

    // Focus first focusable in root level.
    const targets = visibleFocusable(rootLevel);
    if (targets.length) targets[0].focus();
  }

  function closeNav(returnFocus = true) {
    panel.classList.remove('is-open');

    toggle.classList.remove('is-active');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', 'Open menu');

    // Reset all sub-levels immediately.
    resetSubLevels();

    function onDone() {
      panel.hidden = true;
      unlockScroll();
      if (returnFocus) toggle.focus();
    }

    if (reducedMotion) {
      onDone();
    } else {
      panel.addEventListener('transitionend', function handler(e) {
        if (e.target !== panel) return;
        panel.removeEventListener('transitionend', handler);
        onDone();
      });
    }
  }

  function resetSubLevels() {
    subLevels.forEach((level) => {
      level.classList.remove('is-active');
      level.hidden = true;
    });
    rootLevel.classList.remove('is-behind');
    activeSubLevel = null;
    activeTrigger = null;

    drillTriggers.forEach((btn) =>
      btn.setAttribute('aria-expanded', 'false')
    );
  }

  // ── Drill down / up ──

  function drillDown(trigger) {
    const targetId = trigger.getAttribute('aria-controls');
    const target = document.getElementById(targetId);
    if (!target) return;

    activeTrigger = trigger;
    activeSubLevel = target;

    trigger.setAttribute('aria-expanded', 'true');
    rootLevel.classList.add('is-behind');

    target.hidden = false;
    void target.offsetHeight;
    target.classList.add('is-active');

    // Focus the back button in the sub-level.
    const back = target.querySelector('.mobile-nav-back');
    if (back) back.focus();
  }

  function drillUp() {
    if (!activeSubLevel) return;

    const sub = activeSubLevel;
    const trigger = activeTrigger;

    sub.classList.remove('is-active');
    rootLevel.classList.remove('is-behind');

    function onDone() {
      sub.hidden = true;
      activeSubLevel = null;
      activeTrigger = null;
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.focus();
      }
    }

    if (reducedMotion) {
      onDone();
    } else {
      sub.addEventListener('transitionend', function handler(e) {
        if (e.target !== sub) return;
        sub.removeEventListener('transitionend', handler);
        onDone();
      });
    }
  }

  // ── Focus trap ──

  function trapFocus(e) {
    if (e.key !== 'Tab') return;

    const level = getActiveLevel();
    const scrollContainer = level.querySelector('.mobile-nav-scroll') || level;
    const focusable = visibleFocusable(scrollContainer);
    if (focusable.length === 0) return;

    // Include the hamburger toggle (close button) in the root level trap.
    const includeToggle = !activeSubLevel;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (includeToggle && document.activeElement === toggle) {
        e.preventDefault();
        last.focus();
      } else if (document.activeElement === first) {
        e.preventDefault();
        if (includeToggle) {
          toggle.focus();
        } else {
          last.focus();
        }
      }
    } else {
      if (includeToggle && document.activeElement === toggle) {
        e.preventDefault();
        first.focus();
      } else if (document.activeElement === last) {
        e.preventDefault();
        if (includeToggle) {
          toggle.focus();
        } else {
          first.focus();
        }
      }
    }
  }

  // ── Event listeners ──

  toggle.addEventListener('click', () => {
    if (panel.hidden || !panel.classList.contains('is-open')) {
      openNav();
    } else {
      closeNav(true);
    }
  });

  drillTriggers.forEach((btn) => {
    btn.addEventListener('click', () => drillDown(btn));
  });

  backBtns.forEach((btn) => {
    btn.addEventListener('click', () => drillUp());
  });

  // Utility buttons — open the existing popovers in modal mode.
  const popoverMap = {
    search: document.getElementById('search-popover'),
    translate: document.getElementById('translate-popover'),
  };

  utilBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      const action = btn.dataset.action;
      const popoverEl = popoverMap[action];
      if (!popoverEl || !popoverEl.__popoverInstance) return;

      closeNav(false);
      // Open on next frame so the current click finishes propagating
      // before the popover's outside-click handler is active.
      requestAnimationFrame(() => {
        popoverEl.__popoverInstance.open();
      });
    });
  });

  // Sticky back button shadow — toggle .is-stuck when scrolled.
  subLevels.forEach((level) => {
    const scroll = level.querySelector('.mobile-nav-scroll');
    const back = level.querySelector('.mobile-nav-back');
    if (!scroll || !back) return;

    scroll.addEventListener('scroll', () => {
      back.classList.toggle('is-stuck', scroll.scrollTop > 0);
    }, { passive: true });
  });

  // Keyboard: Tab on the toggle while nav is open — trap into the panel.
  toggle.addEventListener('keydown', (e) => {
    if (panel.hidden || !panel.classList.contains('is-open')) return;
    trapFocus(e);
  });

  // Keyboard: Escape and focus trap.
  panel.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (activeSubLevel) {
        drillUp();
      } else {
        closeNav(true);
      }
      return;
    }

    trapFocus(e);
  });
}

// Initialize.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMobileNav);
} else {
  initMobileNav();
}
